import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import { isUpdatedFromDefault } from "../../utils";

import {
  ColorInput,
  UnitInput,
  Tabs,
  PaddingInput,
  MarginInput,
  Spacer,
  AlignInput,
  ToggleInput,
  Panel,
  BorderInput,
  BorderRadiusInput,
  BoxShadowInput,
  TextInput,
  MultipleSelect,
  EditPopover,
} from "../../input";

import { supportLibrary } from "../../block-supports";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import Toast from "../../toast/toast";
import { toast } from "react-toastify";
import { INPUT_LIMITS } from "../../input-limits";

export const FormCheckboxSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const [newName, setNewName] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const {
    width,
    height,
    inputWidth,
    inputHeight,
    labelFontSize,
    labelColor,
    inputColor,
    containerBackgroundColor,
    inputMargin,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    inputPadding,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    align,
    alignTablet,
    alignMobile,
    checked,
    options = [],
    inputBorder,
    inputBorderRadius,
    layoutBorder,
    layoutBorderRadius,
    layoutBoxShadow,
    inputBoxShadow,
    gapX = "8px",
    gapY = "5px",
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <EditPopover label={"Options"} canReset={false}>
            <div>
              {options && options.length > 0 && (
                <>
                  <h6 className="text-xs cursor-default text-white ">
                    Options
                  </h6>
                  <div className="my-2">
                    {options.map((item, i) => {
                      return (
                        <div
                          key={`option-${i}`}
                          title={item.name}
                          className="flex gap-2 justify-between truncate  bg-[#333] p-2"
                        >
                          <p className="text-white text-xs truncate width-[100px]">
                            {item.label}
                          </p>
                          <FontAwesomeIcon
                            icon={faXmark}
                            color="#fff"
                            className="cursor-pointer"
                            onClick={(e) => {
                              const tempOptions = [...options];
                              tempOptions.splice(i, 1);
                              setProp((props) => {
                                props.options = tempOptions;
                              });
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <h6 className="text-xs cursor-default text-white ">
                Add New Option
              </h6>
              <TextInput
                minLength={INPUT_LIMITS["form-input-name"].minlength}
                maxLength={INPUT_LIMITS["form-input-name"].maxlength}
                resetValue={undefined}
                value={newName}
                onChange={(val) => {
                  setNewName(val);
                }}
                label={"Name"}
              />
              <TextInput
                minLength={INPUT_LIMITS["form-input-label"].minlength}
                maxLength={INPUT_LIMITS["form-input-label"].maxlength}
                resetValue={undefined}
                value={newLabel}
                onChange={(val) => {
                  setNewLabel(val);
                }}
                label={"Label"}
              />
              <button
                onClick={(e) => {
                  if (!newName || !newLabel) {
                    toast.error(
                      <Toast
                        title={"Error"}
                        description={"Name/Label Empty."}
                      />,
                    );
                    return;
                  }
                  const tempOptions = [...options];
                  tempOptions.push({ name: newName, label: newLabel });
                  setProp((props) => {
                    props.options = tempOptions;
                  });
                  setNewLabel("");
                  setNewName("");
                }}
                className="p-1 w-12 text-xs border bg-editor-bg-main grigora-text-input__field"
              >
                Add
              </button>
            </div>
          </EditPopover>

          <UnitInput
            value={gapY}
            onChange={(val) => {
              setProp((props) => {
                props.gapY = val;
              });
            }}
            resetValue="5px"
            label={"Gap (Horizontal)"}
          />
          <UnitInput
            value={gapX}
            onChange={(val) => {
              setProp((props) => {
                props.gapX = val;
              });
            }}
            resetValue="8px"
            label={"Gap (Vertical)"}
          />
          <UnitInput
            resetValue={undefined}
            value={inputWidth}
            onChange={(val) => {
              setProp((props) => {
                props.inputWidth = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={inputHeight}
            onChange={(val) => {
              setProp((props) => {
                props.inputHeight = val;
              });
            }}
            label={"Height"}
          />
          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: containerBackgroundColor },
            ])}
            id={`block-setting-${id}-container`}
            title={"Container"}
            initialOpen={true}
          >
            <ColorInput
              value={containerBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.containerBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([{ key: inputColor }])}
            id={`block-setting-${id}-input`}
            title={"Input"}
            initialOpen={true}
          >
            <ColorInput
              value={inputColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputColor = val;
                });
              }}
              label={"Color"}
            />
          </Panel>

          <Panel
            isUpdated={isUpdatedFromDefault([{ key: labelColor }])}
            id={`block-setting-${id}-label`}
            title={"Label"}
            initialOpen={true}
          >
            <ColorInput
              value={labelColor}
              onChange={(val) => {
                setProp((props) => {
                  props.labelColor = val;
                });
              }}
              label={"Color"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
          ])}
          id={`block-setting-${id}-container-layout`}
          initialOpen={false}
          title={"Container Layout"}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            value={layoutPadding}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputPadding },
            { key: inputMargin },
          ])}
          id={`block-setting-${id}-input-layout`}
          initialOpen={false}
          title={"Input Layout"}
        >
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            value={inputPadding}
            onChange={(val) => {
              setProp((props) => {
                props.inputPadding = val;
              });
            }}
          />
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            value={inputMargin}
            onChange={(val) => {
              setProp((props) => {
                props.inputMargin = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutBorder },
            { key: layoutBorderRadius },
          ])}
          id={`block-setting-${id}-container-border`}
          initialOpen={false}
          title={"Container Border"}
        >
          <BorderInput
            resetValue={undefined}
            value={layoutBorder}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={layoutBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>

        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputBorder },
            { key: inputBorderRadius },
          ])}
          id={`block-setting-${id}-input-border`}
          initialOpen={false}
          title={"Input Border"}
        >
          <BorderInput
            resetValue={undefined}
            value={inputBorder}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={inputBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: layoutBoxShadow }])}
          id={`block-setting-${id}-container-boxshadow`}
          initialOpen={false}
          title={"Container Shadows"}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={layoutBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBoxShadow = val;
              });
            }}
            label={"Box Shadow"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: inputBoxShadow }])}
          id={`block-setting-${id}-input-boxshadow`}
          initialOpen={false}
          title={"Input Boxshadows"}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={inputBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.inputBoxShadow = val;
              });
            }}
            label={"Box Shadow"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: newName },
        { key: newLabel },
        { key: gapY, default: "8px" },
        { key: gapX, default: "5px" },
        { key: inputWidth },
        { key: inputHeight },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: containerBackgroundColor },
        { key: inputColor },
        { key: labelColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutBorder },
        { key: layoutBorderRadius },
        { key: layoutBoxShadow },
        { key: inputPadding },
        { key: inputMargin },
        { key: inputBorder },
        { key: inputBorderRadius },
        { key: inputBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
